import { Injectable } from '@angular/core';

type Coordinate = { lat: number; lng: number };

@Injectable({
  providedIn: 'root',
})
export class StationRangeMapGeometryService {
  normalizeStationRange(stationRange: any) {
    if (!stationRange) return null;

    const from = stationRange.from || {};
    const to = stationRange.to || {};
    const normalized = {
      alignment: stationRange.alignment || null,
      from: {
        station: from.station || null,
        offsetType: from.offsetType ?? from.offset_type ?? null,
        offsetDistance: from.offsetDistance ?? from.offset_distance ?? null,
      },
      to: {
        station: to.station || null,
        offsetType: to.offsetType ?? to.offset_type ?? null,
        offsetDistance: to.offsetDistance ?? to.offset_distance ?? null,
      },
    };

    const hasValue = [
      normalized.alignment,
      normalized.from.station,
      normalized.from.offsetType,
      normalized.from.offsetDistance,
      normalized.to.station,
      normalized.to.offsetType,
      normalized.to.offsetDistance,
    ].some(value => value !== null && value !== undefined && value !== '');

    return hasValue ? normalized : null;
  }

  buildStationRangeGeometries(stationRange: any, alignmentGeometry: any, baseAttributes: any = {}) {
    const mapData = this.buildStationRangeMapData(stationRange, alignmentGeometry);
    if (!mapData) return [];

    const attributes = {
      ...baseAttributes,
      is_station_range: true,
      station_range_alignment: mapData.alignmentName,
    };
    const geometries = [];

    if (mapData.alignmentPath?.length >= 2) {
      geometries.push({
        type: 'LineString',
      coordinates: mapData.alignmentPath.map(point => [point.lng, point.lat]),
      color: '#8EA0D8',
      opacity: 0.45,
      strokeWidth: 2,
      station_range_part: 'alignment',
      ...attributes,
    });
    }

    // Single-station (FROM only): a dot on the alignment, plus the offset tie-line
    // and offset dot when an offset is set. No range line / end caps.
    if (mapData.isSingleStation) {
      const station = mapData.singleStationPosition;
      const offset = mapData.fromOffsetPoint;
      if (station && offset) {
        geometries.push({
          type: 'LineString',
          coordinates: [[station.lng, station.lat], [offset.lng, offset.lat]],
          color: '#1036D6',
          strokeWidth: 1.5,
          station_range_part: 'offset_tie_line',
          ...attributes,
        });
      }
      const dot = offset || station;
      if (dot) {
        geometries.push({
          type: 'Point',
          coordinates: [dot.lng, dot.lat],
          color: '#1036D6',
          markerIconUrl: '/assets/icons/map/markers/offset-ring.svg',
          iconSize: { width: 10, height: 10, yOffset: 5 },
          station_range_part: 'single_station',
          ...attributes,
        });
      }
      return geometries;
    }

    geometries.push({
      type: 'LineString',
      coordinates: mapData.selectedPath.map(point => [point.lng, point.lat]),
      color: '#1036D6',
      lineStyle: 'short-dash',
      strokeWidth: 4,
      station_range_part: 'selected',
      ...attributes,
    });

    mapData.endCapPaths.forEach(capPath => {
      geometries.push({
        type: 'LineString',
        coordinates: capPath.map(point => [point.lng, point.lat]),
        color: '#1036D6',
        strokeWidth: 4,
        station_range_part: 'end_cap',
        ...attributes,
      });
    });

    [
      { endpoint: mapData.selectedPath[0], offset: mapData.fromOffsetPoint },
      { endpoint: mapData.selectedPath[mapData.selectedPath.length - 1], offset: mapData.toOffsetPoint },
    ].forEach(({ endpoint, offset }) => {
      if (!offset || !endpoint) return;
      geometries.push({
        type: 'LineString',
        coordinates: [[endpoint.lng, endpoint.lat], [offset.lng, offset.lat]],
        color: '#1036D6',
        strokeWidth: 1.5,
        station_range_part: 'offset_tie_line',
        ...attributes,
      });
      geometries.push({
        type: 'Point',
        coordinates: [offset.lng, offset.lat],
        color: '#1036D6',
        markerIconUrl: '/assets/icons/map/markers/offset-ring.svg',
        iconSize: { width: 10, height: 10, yOffset: 5 },
        station_range_part: 'offset_dot',
        ...attributes,
      });
    });

    return geometries;
  }

  getStationRangeLocation(stationRange: any, alignmentGeometry: any): { latitude: number; longitude: number } | null {
    const mapData = this.buildStationRangeMapData(stationRange, alignmentGeometry);
    if (!mapData?.center) return null;

    return {
      latitude: mapData.center.lat,
      longitude: mapData.center.lng,
    };
  }

  buildStationRangeMapData(stationRange: any, alignmentGeometry: any) {
    const normalizedStationRange = this.normalizeStationRange(stationRange);
    if (!normalizedStationRange) return null;

    const alignments = this.parseAlignmentGeometry(alignmentGeometry);
    if (!alignments.length) return null;

    const alignment = normalizedStationRange.alignment
      ? alignments.find(item => item.name === normalizedStationRange.alignment)
      : alignments[0];
    if (!alignment) return null;

    const selectedPath = this.getStationRangePolyline({
      alignment,
      fromStationName: normalizedStationRange.from?.station,
      toStationName: normalizedStationRange.to?.station,
    });

    const fromOffsetPoint = this.getOffsetStationPosition({
      alignment,
      stationName: normalizedStationRange.from?.station,
      offsetType: normalizedStationRange.from?.offsetType,
      offsetDistance: normalizedStationRange.from?.offsetDistance,
    });
    const toOffsetPoint = this.getOffsetStationPosition({
      alignment,
      stationName: normalizedStationRange.to?.station,
      offsetType: normalizedStationRange.to?.offsetType,
      offsetDistance: normalizedStationRange.to?.offsetDistance,
    });
    const hasFromOffset = this.hasNonZeroOffset(normalizedStationRange.from);
    const hasToOffset = this.hasNonZeroOffset(normalizedStationRange.to);

    // Single-station fallback: a FROM-only station range (or one whose TO can't
    // be resolved) has no range line. Represent it as a single point on the
    // alignment so the card mini-map, the map drilldown, and location derivation
    // (no-location classification) still work — mirroring the FROM-only preview.
    if (selectedPath.length < 2) {
      const singleStationPosition =
        this.getStationPositionOnAlignment({ alignment, stationName: normalizedStationRange.from?.station })?.coordinate
        || this.getStationPositionOnAlignment({ alignment, stationName: normalizedStationRange.to?.station })?.coordinate;
      if (!singleStationPosition) return null;

      const singleOffsetPoint = hasFromOffset ? fromOffsetPoint : (hasToOffset ? toOffsetPoint : null);
      return {
        alignmentName: alignment.name,
        alignmentPath: alignment.polylineCoordinates,
        selectedPath: [],
        isSingleStation: true,
        singleStationPosition,
        endCapPaths: [],
        fromPosition: singleStationPosition,
        toPosition: null,
        fromOffsetPoint: singleOffsetPoint,
        toOffsetPoint: null,
        center: singleOffsetPoint || singleStationPosition,
      };
    }

    return {
      alignmentName: alignment.name,
      alignmentPath: alignment.polylineCoordinates,
      selectedPath,
      isSingleStation: false,
      singleStationPosition: null,
      endCapPaths: this.getStationRangeEndCapPaths(selectedPath[0], selectedPath[selectedPath.length - 1]),
      fromPosition: selectedPath[0],
      toPosition: selectedPath[selectedPath.length - 1],
      fromOffsetPoint: hasFromOffset ? fromOffsetPoint : null,
      toOffsetPoint: hasToOffset ? toOffsetPoint : null,
      center: this.getMapCenter(selectedPath),
    };
  }

  hasNonZeroOffset(side: any) {
    return !!(side?.offsetType && Number(side?.offsetDistance) > 0
      && this.getOffsetDirectionMultiplier(side.offsetType) !== 0);
  }

  parseStationToFeet(stationName: string): number | null {
    const match = `${stationName || ''}`.match(/^(\d+)\+(\d+)$/);
    if (!match) return null;
    return parseInt(match[1], 10) * 100 + parseInt(match[2], 10);
  }

  getOffsetDirectionMultiplier(offsetType: string) {
    if (`${offsetType || ''}`.toUpperCase().startsWith('RT')) return -1;
    if (`${offsetType || ''}`.toUpperCase().startsWith('LT')) return 1;
    return 0;
  }

  getSignedOffsetFeet({ offsetType, offsetDistance }) {
    const distance = parseFloat(offsetDistance);
    return this.getOffsetDirectionMultiplier(offsetType) * (Number.isFinite(distance) ? distance : 0);
  }

  getOffsetCoordinate({ coordinate, previousCoordinate, nextCoordinate, offsetFeet }) {
    if (!coordinate || !offsetFeet) return coordinate;

    const tangentStart = previousCoordinate || coordinate;
    const tangentEnd = nextCoordinate || coordinate;
    const earthRadiusFeet = 20902231;
    const toRad = deg => (deg * Math.PI) / 180;
    const toDeg = rad => (rad * 180) / Math.PI;
    const cosLat = Math.max(Math.cos(toRad(coordinate.lat)), 0.000001);
    const dx = toRad(tangentEnd.lng - tangentStart.lng) * earthRadiusFeet * cosLat;
    const dy = toRad(tangentEnd.lat - tangentStart.lat) * earthRadiusFeet;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (!length) return coordinate;

    const leftNormalX = -dy / length;
    const leftNormalY = dx / length;
    const offsetX = leftNormalX * offsetFeet;
    const offsetY = leftNormalY * offsetFeet;

    return {
      lat: coordinate.lat + toDeg(offsetY / earthRadiusFeet),
      lng: coordinate.lng + toDeg(offsetX / (earthRadiusFeet * cosLat)),
    };
  }

  getStationPositionOnAlignment({ alignment, stationName }) {
    const stationFeet = this.parseStationToFeet(stationName);
    const stations = alignment?.stations || [];
    if (stationFeet == null || stations.length < 2) return null;

    for (let index = 0; index < stations.length - 1; index++) {
      const segmentStartFeet = this.parseStationToFeet(stations[index].name);
      const segmentEndFeet = this.parseStationToFeet(stations[index + 1].name);
      const segmentStart = stations[index].coordinate;
      const segmentEnd = stations[index + 1].coordinate;

      if (segmentStartFeet == null || segmentEndFeet == null || !segmentStart || !segmentEnd) {
        continue;
      }

      const segmentLow = Math.min(segmentStartFeet, segmentEndFeet);
      const segmentHigh = Math.max(segmentStartFeet, segmentEndFeet);
      if (stationFeet < segmentLow || stationFeet > segmentHigh) {
        continue;
      }

      const ratio = segmentEndFeet === segmentStartFeet
        ? 0
        : (stationFeet - segmentStartFeet) / (segmentEndFeet - segmentStartFeet);

      return {
        coordinate: {
          lat: segmentStart.lat + ratio * (segmentEnd.lat - segmentStart.lat),
          lng: segmentStart.lng + ratio * (segmentEnd.lng - segmentStart.lng),
        },
        previousCoordinate: segmentStart,
        nextCoordinate: segmentEnd,
      };
    }

    return null;
  }

  getOffsetStationPosition({ alignment, stationName, offsetType, offsetDistance }) {
    const stationPosition = this.getStationPositionOnAlignment({ alignment, stationName });
    if (!stationPosition) return null;

    return this.getOffsetCoordinate({
      coordinate: stationPosition.coordinate,
      previousCoordinate: stationPosition.previousCoordinate,
      nextCoordinate: stationPosition.nextCoordinate,
      offsetFeet: this.getSignedOffsetFeet({ offsetType, offsetDistance }),
    });
  }

  makeCoordinate(coordinate: any): Coordinate | null {
    if (!coordinate) return null;
    if (Array.isArray(coordinate) && coordinate.length >= 2) {
      return { lat: Number(coordinate[1]), lng: Number(coordinate[0]) };
    }

    const lat = Number(coordinate.lat ?? coordinate.latitude);
    const lng = Number(coordinate.lng ?? coordinate.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return null;
  }

  parseAlignmentGeometry(alignmentGeometry: any) {
    try {
      const geojson = typeof alignmentGeometry === 'string'
        ? JSON.parse(alignmentGeometry)
        : alignmentGeometry;
      if (!geojson?.features?.length) return [];

      return geojson.features.map(feature => {
        const coordinates = feature?.geometry?.coordinates || [];
        const rawStations = feature?.properties?.stations || [];
        const stations = rawStations.map((station, index) => {
          const coordinate = this.makeCoordinate(coordinates[index]);
          return coordinate ? { name: station?.name, coordinate } : null;
        }).filter(Boolean);

        return {
          name: feature?.properties?.name || '',
          polylineCoordinates: coordinates.map(coordinate => this.makeCoordinate(coordinate)).filter(Boolean),
          stations,
        };
      });
    } catch {
      return [];
    }
  }

  getStationRangePolyline({ alignment, fromStationName, toStationName }) {
    const fromFeet = this.parseStationToFeet(fromStationName);
    const toFeet = this.parseStationToFeet(toStationName);
    const stations = alignment?.stations || [];

    if (fromFeet == null || toFeet == null || stations.length < 2 || !alignment?.polylineCoordinates?.length) {
      return [];
    }

    const rangeStart = Math.min(fromFeet, toFeet);
    const rangeEnd = Math.max(fromFeet, toFeet);
    const points = [];

    for (let index = 0; index < stations.length - 1; index++) {
      const segmentStartFeet = this.parseStationToFeet(stations[index].name);
      const segmentEndFeet = this.parseStationToFeet(stations[index + 1].name);
      const segmentStart = stations[index].coordinate;
      const segmentEnd = stations[index + 1].coordinate;

      if (segmentStartFeet == null || segmentEndFeet == null || !segmentStart || !segmentEnd) {
        continue;
      }

      const segmentLow = Math.min(segmentStartFeet, segmentEndFeet);
      const segmentHigh = Math.max(segmentStartFeet, segmentEndFeet);
      const overlapStart = Math.max(rangeStart, segmentLow);
      const overlapEnd = Math.min(rangeEnd, segmentHigh);

      if (overlapStart > overlapEnd) {
        continue;
      }

      const coordinateForFeet = feet => {
        const ratio = segmentEndFeet === segmentStartFeet
          ? 0
          : (feet - segmentStartFeet) / (segmentEndFeet - segmentStartFeet);
        return {
          lat: segmentStart.lat + ratio * (segmentEnd.lat - segmentStart.lat),
          lng: segmentStart.lng + ratio * (segmentEnd.lng - segmentStart.lng),
        };
      };

      const startCoordinate = coordinateForFeet(overlapStart);
      const endCoordinate = coordinateForFeet(overlapEnd);
      const lastPoint = points[points.length - 1];

      if (!lastPoint || lastPoint.lat !== startCoordinate.lat || lastPoint.lng !== startCoordinate.lng) {
        points.push(startCoordinate);
      }
      points.push(endCoordinate);
    }

    return points;
  }

  getMapCenter(points: any[]) {
    const totals = points.reduce((acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }), { lat: 0, lng: 0 });

    return {
      lat: totals.lat / points.length,
      lng: totals.lng / points.length,
    };
  }

  getStationRangeEndCapPaths(start: any, end: any) {
    if (!start || !end) return [];

    const meanLatRad = ((start.lat + end.lat) / 2) * Math.PI / 180;
    const cosLat = Math.max(Math.cos(meanLatRad), 0.000001);
    const latDelta = end.lat - start.lat;
    const lngDeltaScaled = (end.lng - start.lng) * cosLat;
    const length = Math.sqrt(latDelta * latDelta + lngDeltaScaled * lngDeltaScaled);
    if (!length) return [];

    const perpendicularLat = -lngDeltaScaled / length;
    const perpendicularLngScaled = latDelta / length;
    const capLengthDegrees = Math.max(length * 0.16, 0.000055);
    const halfCapLength = capLengthDegrees / 2;

    const capForPoint = point => [
      {
        lat: point.lat - perpendicularLat * halfCapLength,
        lng: point.lng - (perpendicularLngScaled * halfCapLength) / cosLat,
      },
      {
        lat: point.lat + perpendicularLat * halfCapLength,
        lng: point.lng + (perpendicularLngScaled * halfCapLength) / cosLat,
      },
    ];

    return [capForPoint(start), capForPoint(end)];
  }
}
